import { useState, useContext, useMemo, useEffect } from "react";
import { Modal, Button, Col, Row } from "react-bootstrap";
import { i18nValue, currencyFormat, getLocale } from "../../functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faArrowCircleLeft, faImage } from "@fortawesome/free-solid-svg-icons";
import { getCDNImage } from '../../globals';
import { T } from "react-translator-component";
import { Warning } from "../Icons";
import DateSelector from "./DateSelectorModal";
import { format, parseISO } from 'date-fns';
import { usePersistentState } from "../../hooks/usePersistentState";
import { HotelContext } from "../../context/hotel";
import { CheckInContext } from "../../context/checkin";
import processHtml from '../processHtml';
import Settings from "./Settings";
import { getQuantityFromValues } from "./QuestionTotals.utils";
import { shouldHidePrice, getFromPriceLabel, parseOptionSettings, hasSettingsQuestions } from "./upSellingUtils";

const UpSellingCardDetail = ({ show, handleClose, cup, lang, inCart, addToCart, removeFromCart, currency, path }) => {

    const { data: hotel } = useContext(HotelContext)
    const { data: checkIn } = useContext(CheckInContext);
    // const [showDateSelector, setShowDateSelector] = useState(false);
    const [bookData, setBookData] = usePersistentState(`bookData-${cup.id}`, null, 5);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedSetting, setSelectedSetting] = useState(null);

    //estado para guardar los datos del carrito actual
    const [currentCartData, setCurrentCartData] = useState(null);

    useEffect(() => {
        if (cup?.type === "items") return; // Paquete: no auto-seleccionar opción
        if (cup?.options?.length == 1) {
            if (cup.options[0].id) {
                setSelectedOption(cup.options[0].id);
            } else {
                setSelectedOption(null);
            }
        }
    }, [cup]);

    const currentOption = useMemo(() => {
        if (!selectedOption) return null;
        const originalOpt = cup.options.find(option => option.id === selectedOption);
        let opt = { ...originalOpt }
        opt.type = "options";
        // ✅ Guardar el precio base original para usar en Settings.js
        opt.basePrice = originalOpt.price;
        if (selectedSetting) {
            console.log('28dic selectedSetting', selectedSetting);
            const qty = getQuantityFromValues(selectedSetting, originalOpt.settings);
            if (selectedSetting.length == 1 && qty > 0) {
                opt.type = "settings";
                opt.qty = qty;
                opt.price = qty * originalOpt.price
            }
            //buscar en los items de selectedSetting si existe un item con la key total, si existe, setear el price y el qty
            if (selectedSetting.length > 1) {
                const totalItem = selectedSetting.find(item => item.total !== undefined);
                if (totalItem) {
                    console.log('28dic totalItem', totalItem);
                    opt.type = "settings";
                    opt.qty = 1;
                    opt.price = parseInt(totalItem.total);
                    opt.values = selectedSetting;
                    console.log('28dic opt', opt);
                }
            }
        }
        console.log('28dic currentOption', opt);
        return opt;
    }, [selectedOption, selectedSetting]);

    const step = useMemo(() => {
        // Paquete (type "items"): siempre vista info, sin selector de opción
        if (cup?.type === "items") return "info";

        if (!inCart) {
            if (cup.options && !selectedOption) {
                if (cup.options.length == 1) {
                    return "options-selector-one-option";
                } else {
                    return "options-selector";
                }
            }
            // if (!cup.options) return "options-selector-one-option";
            console.log('12dic currentOption.bookableItemId', currentOption);
            if (currentOption && currentOption.bookableItemId && currentOption.BookableItem?.allDay != true) {
                return "form";
            }
            // Solo mostrar settings cuando hay questions reales.
            // Si settings viene como "{}" no debe bloquear el flujo bookable/form.
            if (currentOption && hasSettingsQuestions(currentOption)) {
                return "settings";
            }
        }
        return "info"
    }, [cup, selectedOption, inCart, currentOption, selectedSetting])

    const handleAddToCart = (day, hour, qty, allDay = false, multiplyPrice = false) => {
        if (currentOption?.bookableItemId && day && (hour || allDay)) {
            setBookData({ day, hour, qty });
            let _cup = {
                ...cup,
                optionSelected: { ...currentOption },
                bookData: {
                    day: day,
                    hour: hour,
                    allDay: allDay,
                    qty: qty
                },
            };
            addToCart(_cup);
            handleClose();
        } else if (currentOption?.bookableItemId && !inCart) {
            let _cup = {
                ...cup,
                optionSelected: { ...currentOption }
            };
            if (multiplyPrice) {
                _cup.price = parseInt(qty) * cup.price;
                _cup.optionSelected.price = parseInt(qty) * _cup.optionSelected.price;
            }
            addToCart(_cup);
            handleClose();

        } else if (!cup?.bookableItemId) {
            addToCart(cup);
            handleClose();
        };
    }
    const handleRemoveFromCart = () => {
        removeFromCart(cup.id)
        setBookData(null);
        handleClose();
    }

    const handleSettingsOnChange = (el) => {
        console.log('28dic - handleSettingsOnChange', el);
        if (el.length > 0) {
            setSelectedSetting(el);
        } else {
            setSelectedSetting(null);
        }
    }

    const handleDateSelectorClose = () => {
        if (step == "form" && cup.options?.length > 1) {
            setSelectedOption(null);
        } else {
            handleClose();
        }
    }

    const handleSettingAddToCart = (values) => {
        let auxCurrentOption = { ...currentOption }
        const basePrice = auxCurrentOption.basePrice ?? auxCurrentOption.price;
        let _qty = parseInt(auxCurrentOption.qty || 1);
        const qtyFromValues = getQuantityFromValues(values, currentOption?.settings);
        if (qtyFromValues > 0) {
            _qty = qtyFromValues;
        } else if (values?.[0]?.qty != null) {
            _qty = parseInt(values[0].qty);
        } else if (currentOption.BookableItem?.allDay == true && values?.find(v => v?.dates)) {
            _qty = values.find(v => v?.dates)?.dates?.length ?? 1;
        }

        // ✅ Prioridad 1: si QuestionTotals puso un total en values, usarlo
        const totalFromValues = values?.find(v => v?.total != null && v?.total > 0)?.total;
        if (totalFromValues != null) {
            auxCurrentOption.price = totalFromValues;
        } else if (currentOption.type === "settings") {
            // Precio ya venía calculado desde el carrito/selectedSetting
            auxCurrentOption.price = auxCurrentOption.price ?? basePrice;
        } else {
            // Sin total en values: precio por unidad × cantidad
            auxCurrentOption.price = _qty * basePrice;
        }

        // Se obliga a que el qty sea 1 si es allDay
        if(currentOption.BookableItem?.allDay == true){
            _qty = 1;
        }
        
        let settingData = {
            qty: _qty,
            price: basePrice,
            total: auxCurrentOption.price
        }
        if (values) {
            settingData = { ...settingData, values: JSON.stringify(values) }
        }

        let _cup = {
            ...cup,
            optionSelected: auxCurrentOption,
            settingData: settingData
        };

        setCurrentCartData(_cup);
        addToCart(_cup);
        handleClose();
    }
    const handleSettingsClose = () => {
        handleClose();
    }

    // ✅ useMemo para evaluar el precio mostrado con etiqueta "desde: " si hay múltiples opciones
    // Usa currentOption.price (ya calculado correctamente) si existe, sino cup.price
    const { displayPrice, priceLabel } = useMemo(() => {
        let price = 0;
        let label = "";

        // Paquete (type "items"): mostrar precio del paquete
        if (cup?.type === "items" && cup?.price != null) {
            return { displayPrice: cup.price, priceLabel: label };
        }
        
        if (currentOption?.price !== undefined && currentOption.price !== null) {
            price = currentOption.price;
        } else {
            price = cup?.price || 0;
        }
        
        // ✅ Si hay múltiples opciones y no está seleccionado nada, mostrar el menor precio con "desde: "
        if (cup?.options && cup.options.length > 1 && !selectedOption) {
            const minPrice = Math.min(...cup.options.map(opt => opt.price || 0));
            price = minPrice;
            label = getFromPriceLabel() || "desde: ";
        }
        
        return { displayPrice: price, priceLabel: label };
    }, [currentOption, cup, selectedOption]);

    const hidePriceBadge = shouldHidePrice(cup);

    const handleSimpleAddToCart = () => {
        console.log('29ene - handleSimpleAddToCart - currentOption', currentOption)

        // Paquete (type "items"): añadir como un solo ítem con lista de productos
        if (cup?.type === "items") {
            const _cup = {
                ...cup,
                optionSelected: {
                    type: "items",
                    price: cup.price,
                    items: cup.options ?? []
                }
            };
            addToCart(_cup);
            handleClose();
            return;
        }

        let _cup = {
            ...cup,
            optionSelected: { ...currentOption }
        };
        addToCart(_cup);
        handleClose();
    }

    return (
        
        <>
            <Modal
                show={show}
                onHide={handleClose}
                className={`cross-up-selling-detail-modal hotel-${hotel.code}`}
            >
                <div className="modal-content-custom">
                    <Modal.Body className="modal-body-custom">

                        {!hidePriceBadge && <div style={{ position: "absolute", right: 10, top: 10, background: "rgba(255, 255, 255, 0.8)", padding: "8px 16px", borderRadius: 8, textAlign: "center" }}>
                            {displayPrice !== 0 ? (
                                <>
                                    <div className="cross-up-selling-detail-modal-price">
                                        {priceLabel && <span style={{ fontSize: '12px', marginRight: '4px', fontWeight: '400' }}>{priceLabel}</span>}
                                        {currency} {displayPrice}
                                    </div>
                                    {cup.priceIsPerNight && <div className="d-block text-muted">{T('cups.priceIsPerNight')}</div>}
                                </>
                            ) : (
                                <p className="text-success text-center font-size-lg mb-0">
                                    <b>{T("general.no-charge")}</b>
                                </p>
                            )}
                        </div>
                        }
                        {cup.photo ? <img variant="top" className="w-100" src={getCDNImage(cup.photo)} style={{ border: "3px solid #FFF", objectFit: "cover", borderRadius: "15px 15px 0 0" }} height={250} /> :
                            <>
                                <div className='w-100 d-flex justify-content-center align-items-center' style={{ backgroundColor: '#C9C9C9', height: 190 }}>
                                    <FontAwesomeIcon icon={faImage} style={{ color: 'white', fontSize: '110px' }} />                                </div>

                            </>}
                        <div className="px-3 py-2">
                            <h4 className="cross-up-selling-detail-modal-title" style={{ fontSize: "1.25rem" }}>{i18nValue(cup, 'title', lang)}

                                {bookData &&

                                    <span className="small d-inline ml-2 text-success">
                                        {`${T('cups.booked')} - ${format(parseISO(bookData.day), " dd MMM yyyy", { locale: getLocale(lang) })} - ${bookData.hour}${T("general.time-format")} -  ${bookData.qty} ${T("cups.spots")}`}
                                    </span>}
                            </h4>
                            {cup?.availabilityStatus && (
                                <div
                                    style={{
                                        margin: "1rem 0",
                                        background: "#ffefbc",
                                        borderRadius: "8px",
                                        padding: "0.8rem 1rem",
                                        fontSize: "0.9rem",
                                        lineHeight: "1.1rem",
                                        color: "#d48200"
                                    }}
                                >
                                    <b style={{ color: "#d48200", textTransform: "uppercase" }}>{T('cups.subject_to_availability')}</b>. {T('cups.subject_to_availability_text')}
                                </div>
                            )}
                            <p className="cross-up-selling-detail-modal-description" style={{ fontSize: "1rem", lineHeight: "1.2rem" }}>{processHtml(i18nValue(cup, 'description', lang))}</p>

                            {step === "info" && cup?.type === "items" && cup?.options?.length > 0 && (
                                <div className="mb-3">
                                    <h6 className="mb-2 font-weight-bold">{T('services.type.items_sales')}</h6>
                                    <ul className="list-unstyled mb-2">
                                        {(cup.options || []).map((opt, idx) => {
                                            const qty = opt.qty ?? opt.quantity ?? 1;
                                            const name = i18nValue(opt, 'name', lang) || T('general.product');
                                            return (
                                                <li key={opt.id || idx} className="d-flex align-items-center py-1">
                                                    <span>{name}{qty > 1 ? ` x${qty}` : ''}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {cup.price != null && (
                                        <div className="font-weight-bold mt-2">{currencyFormat(cup.price, currency)}</div>
                                    )}
                                </div>
                            )}

                            {step === "info" && cup?.type !== "items" && (
                                <>
                                    {currentOption && currentOption?.price > 0 && currentOption?.BookableItem?.allDay != true ? (
                                        <div className="title-container d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0">{i18nValue(currentOption, 'name', lang)}</h6>
                                            <h5 className="mb-0 text-nowrap">{currencyFormat(currentOption?.price, currency)}</h5>
                                        </div>
                                    ):currentOption && currentOption?.BookableItem?.allDay == true && currentCartData?.settingData?.total > 0 && (
                                        <div className="title-container d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0">{i18nValue(currentOption, 'name', lang)}</h6>
                                            {console.log('19dic currentCartData', currentCartData)}
                                            <h5 className="mb-0 text-nowrap">{currencyFormat(currentCartData?.settingData?.total, currency)}</h5>
                                        </div>
                                    )}
                                </>
                            )}

                            {step === "options-selector" && cup?.type !== "items" && <div style={{ background: "#d1e8ff", borderTop: "2px solid #a7c6e5", margin: "40px -16px -12px", padding: "16px" }}>
                                <h6 className="mt-1 mb-3 text-center font-weight-bold">{T('general.select_option')}</h6>
                                {cup.options?.map((opt, idx) => {
                                    return <div
                                        style={{
                                            background: "white",
                                            border: "1px solid #DDD",
                                            color: "#666",
                                            padding: "10px 15px",
                                            borderRadius: 8,
                                            marginBottom: 8,
                                            cursor: "pointer"
                                        }}
                                        onClick={() => setSelectedOption(opt.id)}
                                    >
                                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 30 }}>
                                            <h6 className="mb-0">{i18nValue(opt, 'name', lang)}</h6>
                                            <h5 className="mb-0 text-nowrap font-size-md font-weight-bold">{currencyFormat(opt.price, currency)}</h5>
                                        </div>
                                    </div>
                                })}
                            </div>}

                            {step === "form" && <>
                                <div className="upselling-current-data">
                                    <div className="title-container d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">{i18nValue(currentOption, 'name', lang)}</h6>
                                        <h5 className="mb-0 text-nowrap">{currencyFormat(currentOption?.price, currency)}</h5>
                                    </div>
                                    <div className="form-container">
                                        <DateSelector
                                            bookableItemId={currentOption.bookableItemId}
                                            setShowDateSelector={() => { }}
                                            showDateSelector={true}
                                            //onCancel={() => setSelectedOption(null)}
                                            onCancel={handleDateSelectorClose}
                                            handleAddToCart={handleAddToCart} />
                                    </div>
                                </div>
                            </>
                            }

                            {step === "settings" && <>
                                <div className="settings-container">
                                    <Settings
                                        settings={parseOptionSettings(currentOption.settings) || []}
                                        addon={currentOption}
                                        currency={currency}
                                        handleOnChange={handleSettingsOnChange}
                                        // showButtons={false}
                                        onCancel={handleSettingsClose}
                                        handleAddToCart={handleSettingAddToCart}
                                        language={lang}
                                        checkin={checkIn} />
                                </div>
                            </>
                            }

                        </div>
                    </Modal.Body>

                    {step === "info" && <Modal.Footer className="modal-footer-custom">
                        {(step === "info" || step === "options-selector-one-option") && <Row className="flex-fill">
                            <Col xs={6} className={"p-0 pr-1"}>
                                <Button
                                    size={"lg"}
                                    variant={"secondary"}
                                    type={"button"}
                                    block={true}
                                    onClick={handleClose}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowCircleLeft}
                                        className="mr-2"
                                    />{" "}
                                    {T("general.back")}
                                </Button>
                            </Col>
                            <Col xs={6} className={"p-0 pl-1"}>

                                {!inCart && path !== "request-detail" && <Button
                                    size={"lg"}
                                    variant={"success"}
                                    type={"button"}
                                    block={true}
                                    onClick={handleSimpleAddToCart}
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />{" "}
                                    {T("cups.add_button")}
                                </Button>}



                                {inCart && <Button
                                    size={"lg"}
                                    variant={"danger"}
                                    type={"button"}
                                    block={true}
                                    onClick={handleRemoveFromCart}
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />{" "}
                                    {T("cups.remove_button")}
                                </Button>}
                            </Col>
                        </Row>}
                    </Modal.Footer>}
                </div >
            </Modal >



        </>
    );
};

export default UpSellingCardDetail;
